import { useMemo, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

const EMBED_MODE_KEY = 'intellix_embed_mode';

/**
 * 嵌入模式 hook
 * 通过 URL 参数 mode=embed 判断是否处于嵌入模式
 * 一旦检测到嵌入模式，会保存到 sessionStorage，确保子页面跳转不丢失状态
 */
export const useEmbedMode = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // 判断是否为嵌入模式：优先检查 URL 参数，其次检查 sessionStorage
  const isEmbedMode = useMemo(() => {
    const urlHasEmbed = searchParams.get('mode') === 'embed';
    if (urlHasEmbed) {
      return true;
    }
    // 检查 sessionStorage 中是否已保存嵌入模式
    try {
      return sessionStorage.getItem(EMBED_MODE_KEY) === 'true';
    } catch {
      return false;
    }
  }, [searchParams]);

  // 当 URL 中包含 mode=embed 时，保存到 sessionStorage
  useEffect(() => {
    if (searchParams.get('mode') === 'embed') {
      try {
        sessionStorage.setItem(EMBED_MODE_KEY, 'true');
      } catch {
        // sessionStorage 不可用时忽略
      }
    }
  }, [searchParams]);

  // 需要在嵌入模式下显示头部的二级页面路径
  const embedHeaderPages = [
    '/opm-editor',
    '/opm-editor-legacy',
    '/data-map-editor',
    '/knowledge-graph',
    '/semantic-data-map',
  ];

  // 嵌入模式下是否应该显示头部（仅在指定的二级页面显示，根目录不显示）
  const shouldShowEmbedHeader = useMemo(() => {
    if (!isEmbedMode) return false;
    return embedHeaderPages.some(path => location.pathname === path);
  }, [isEmbedMode, location.pathname]);

  // 嵌入模式下是否应该完全隐藏头部（根目录或非白名单页面）
  const shouldHideHeader = useMemo(() => {
    if (!isEmbedMode) return false;
    return (
      location.pathname === '/' ||
      !embedHeaderPages.some(path => location.pathname === path)
    );
  }, [isEmbedMode, location.pathname]);

  return {
    isEmbedMode,
    shouldShowEmbedHeader,
    shouldHideHeader,
  };
};

export default useEmbedMode;
